import type { MeetingManager } from "amazon-chime-sdk-component-library-react";
import { stopAllMediaTracks } from "./stopAllMediaTracks";

/**
 * Stops all Chime SDK streams using the meeting manager.
 * Errors are logged rather than swallowed so cleanup failures are visible.
 *
 * Finishes with a synchronous stopAllMediaTracks() safety net that calls
 * track.stop() directly on every MediaStream-backed element in the DOM.
 * This ensures camera/mic hardware is released even if the async SDK
 * methods above fail to complete (e.g., during page unload or provider
 * teardown).
 */
export async function stopChimeStreams(
  meetingManager: MeetingManager | null
): Promise<void> {
  if (!meetingManager) return;

  try {
    try {
      await meetingManager.audioVideo?.stopVideoInput();
    } catch (e) {
      console.warn("[stopChimeStreams] Failed to stop video input:", e);
    }
    try {
      await meetingManager.audioVideo?.stopAudioInput();
    } catch (e) {
      console.warn("[stopChimeStreams] Failed to stop audio input:", e);
    }
    try {
      await meetingManager.audioVideo?.stopContentShare();
    } catch (e) {
      console.warn("[stopChimeStreams] Failed to stop content share:", e);
    }
    try {
      const tiles = meetingManager.audioVideo?.getAllVideoTiles() ?? [];
      for (const tile of tiles) {
        const tileId = tile.id();
        meetingManager.audioVideo?.removeVideoTile(tileId);
      }
    } catch (e) {
      console.warn("[stopChimeStreams] Failed to remove video tiles:", e);
    }
  } catch (e) {
    console.warn("[stopChimeStreams] Unexpected error during cleanup:", e);
  }

  // Safety net: directly stop any remaining MediaStream tracks in the DOM.
  // Covers cases where the SDK methods above didn't fully release hardware
  // (e.g., transform device pipelines, partially torn-down providers).
  stopAllMediaTracks();
}
